import React, { useCallback, useEffect, useMemo } from 'react'

import cx from 'classnames'
import { m, Segment, useAnimate } from 'framer-motion'

import styles from './progressBar.module.scss'

type ProgressDirection = 'increase' | 'decrease'

type ProgressBarProps = {
  progressPercentage: number
  previousPercentage?: number
  progressDirection?: ProgressDirection
  exceedsRange?: boolean
  completed?: boolean
}

type WidthAnimation = [{ width: string }, { duration: number }]

const normalDuration = 0.3
const exceedsRangeDuration = 0.6
const instantDuration = 0

export const createIncreaseAnimations = (
  currentPercentage: number,
  previousPercentage: number,
  exceedsRange: boolean,
): WidthAnimation[] => {
  let animations: WidthAnimation[] = []
  if (exceedsRange) {
    switch (previousPercentage) {
      case 100:
        animations = animations.concat([
          [{ width: `${0}%` }, { duration: instantDuration }],
          [{ width: `${100}%` }, { duration: exceedsRangeDuration }],
          [{ width: `${0}%` }, { duration: instantDuration }],
        ])
        break
      case 0:
        animations = animations.concat([
          [{ width: `${0}%` }, { duration: instantDuration }],
          [{ width: `${100}%` }, { duration: exceedsRangeDuration }],
          [{ width: `${0}%` }, { duration: instantDuration }],
        ])
        break
      default:
        animations = animations.concat([
          [{ width: `${previousPercentage}%` }, { duration: instantDuration }],
          [{ width: `${100}%` }, { duration: exceedsRangeDuration }],
          [{ width: `${0}%` }, { duration: instantDuration }],
        ])
    }
    switch (currentPercentage) {
      case 100:
      case 0:
        break
      default:
        animations = animations.concat([[{ width: `${currentPercentage}%` }, { duration: exceedsRangeDuration }]])
    }
    return animations
  }
  switch (previousPercentage) {
    case 100:
      animations = animations.concat([[{ width: `${0}%` }, { duration: instantDuration }]])
      break
    case 0:
    default:
      animations = animations.concat([[{ width: `${previousPercentage}%` }, { duration: instantDuration }]])
      break
  }
  switch (currentPercentage) {
    case 100:
      animations = animations.concat([
        [{ width: `${100}%` }, { duration: normalDuration }],
        [{ width: `${0}%` }, { duration: instantDuration }],
      ])
      break
    case 0:
      break
    default:
      animations = animations.concat([[{ width: `${currentPercentage}%` }, { duration: normalDuration }]])
      break
  }
  return animations
}

export const createDecreaseAnimations = (
  currentPercentage: number,
  previousPercentage: number,
  exceedsRange: boolean,
): WidthAnimation[] => {
  let animations: WidthAnimation[] = []
  if (exceedsRange) {
    switch (previousPercentage) {
      case 100:
      case 0:
        break
      default:
        animations = animations.concat([
          [{ width: `${previousPercentage}%` }, { duration: instantDuration }],
          [{ width: `${0}%` }, { duration: exceedsRangeDuration }],
        ])
        break
    }
    switch (currentPercentage) {
      case 100:
      case 0:
        if (previousPercentage === 100 || previousPercentage === 0) {
          animations = animations.concat([
            [{ width: `${100}%` }, { duration: exceedsRangeDuration }],
            [{ width: `${0}%` }, { duration: instantDuration }],
          ])
        }
        break
      default:
        animations = animations.concat([
          [{ width: `${100}%` }, { duration: instantDuration }],
          [{ width: `${currentPercentage}%` }, { duration: exceedsRangeDuration }],
        ])
        break
    }
    return animations
  }
  switch (previousPercentage) {
    case 0:
      animations = animations.concat([[{ width: `${100}%` }, { duration: instantDuration }]])
      break
    case 100:
    default:
      animations = animations.concat([[{ width: `${previousPercentage}%` }, { duration: instantDuration }]])
      break
  }
  switch (currentPercentage) {
    case 100:
    case 0:
      animations = animations.concat([[{ width: `${0}%` }, { duration: normalDuration }]])
      break
    default:
      animations = animations.concat([[{ width: `${currentPercentage}%` }, { duration: normalDuration }]])
      break
  }
  return animations
}

const getWidthAnimations = (
  currentPercentage: number,
  previousPercentage: number,
  progressDirection: ProgressDirection,
  exceedsRange: boolean,
): WidthAnimation[] => {
  if (progressDirection === 'increase') {
    return createIncreaseAnimations(currentPercentage, previousPercentage, exceedsRange)
  }
  if (progressDirection === 'decrease') {
    return createDecreaseAnimations(currentPercentage, previousPercentage, exceedsRange)
  }
  return []
}

export const ProgressBar = ({
  progressPercentage,
  previousPercentage,
  completed,
  progressDirection = 'increase',
  exceedsRange,
}: ProgressBarProps) => {
  const [scope, animate] = useAnimate()
  const animations = useMemo(
    () => getWidthAnimations(progressPercentage, previousPercentage ?? 0, progressDirection, exceedsRange ?? false),
    [progressPercentage, previousPercentage, progressDirection, exceedsRange],
  )

  const framerAnimation = useCallback(async () => {
    if (!scope.current) {
      return
    }
    const sequence = [] as Segment[]
    for (let i = 0; i < animations.length; i++) {
      sequence.push([scope.current, ...animations[i]])
    }
    try {
      await animate(sequence)
    } catch (err) {
      console.error('animation error', err)
    }
  }, [scope, animations, animate])

  useEffect(() => {
    framerAnimation()
  }, [framerAnimation])

  return (
    <div className={styles.progressBarContainer}>
      <div className={cx(styles.progressBar, { [styles.completed]: completed })}>
        <m.div ref={scope} initial={false} className={styles.progressBarFill} />
      </div>
    </div>
  )
}
